import { transportCloseReason, transportErrorReason } from '~/modules/remote-mic/network/client/network-client';
import { ClientTransport } from '~/modules/remote-mic/network/client/transport/interface';
import { NetworkMessages } from '~/modules/remote-mic/network/messages';
import { ForwardedMessage } from '~/modules/remote-mic/network/server/transport/relay-server-transport';
import { pack, unpack } from '~/modules/remote-mic/network/utils';
import Listener from '~/modules/utils/listener';

/**
 * How long a socket is given to open before the attempt is written off. A phone that walked out of
 * Wi-Fi range gets no refusal, just silence, and the browser's own timeout runs for minutes.
 */
const CONNECT_DEADLINE_MS = 10_000;

/**
 * How long the relay is then given to answer `register-player`. A separate deadline because the
 * socket opening proves nothing about the room: a relay accepts the socket for a code nobody is
 * hosting - a room that has since restarted, or a QR photographed at last week's party - registers
 * the phone against nothing, and simply never replies. Without this the phone sat on a spinner for
 * good, because no close ever arrives to retry or report either.
 */
const REGISTER_DEADLINE_MS = 8_000;

/**
 * The phone's side of the relay. Shared by the WebSockets and PartyKit transports, which differ
 * only in the URL they dial.
 */
export abstract class RelayClientTransport extends Listener<[NetworkMessages]> implements ClientTransport {
  protected abstract getUrl(roomId: string): string;

  private connection: WebSocket | null = null;
  private roomId: string | null = null;
  /** Told when the game's socket is back after a gap the phone rode out without being dropped. */
  public onRoomBack: (() => void) | null = null;

  /** Closes the current socket and reports it. Rebuilt per connection. */
  private hangUpOnCurrent: ((reason: transportCloseReason) => void) | null = null;
  /** Disowns the current socket: whatever it does next is reported to nobody. Rebuilt per connection. */
  private disownCurrent: (() => void) | null = null;

  public connect(
    clientId: string,
    roomId: string,
    onConnect: () => void,
    onClose: (reason: transportCloseReason, originalEvent: Event | null) => void,
    onError: (error: transportErrorReason, originalEvent: Event) => void,
  ): void {
    this.roomId = roomId;

    const connection = new WebSocket(this.getUrl(roomId));
    connection.binaryType = 'arraybuffer';
    this.connection = connection;

    let closeReported = false;
    let registerDeadline: ReturnType<typeof setTimeout> | undefined;
    const reportClosed = (reason: transportCloseReason, event: Event | null) => {
      if (closeReported) return;
      closeReported = true;
      clearTimeout(openDeadline);
      clearTimeout(registerDeadline);
      if (this.connection === connection) {
        this.connection = null;
        this.hangUpOnCurrent = null;
        this.disownCurrent = null;
      }
      this.clearAllListeners();
      onClose(reason, event);
    };

    const hangUp = (reason: transportCloseReason) => {
      connection.close();
      // Reported rather than waited for: `close()` only promises the socket stops being used, not
      // that `onclose` ever arrives - a socket the network has already swallowed can sit in CLOSING
      // indefinitely, and the phone would wait on it forever.
      reportClosed(reason, null);
    };

    const openDeadline = setTimeout(() => hangUp('unreachable'), CONNECT_DEADLINE_MS);
    this.hangUpOnCurrent = hangUp;
    this.disownCurrent = () => {
      closeReported = true;
      clearTimeout(openDeadline);
      clearTimeout(registerDeadline);
    };

    connection.onopen = () => {
      clearTimeout(openDeadline);
      // Reported as its own reason rather than as `unreachable`: the relay was reached perfectly
      // well, and what the phone is told to do about it differs - rescanning the code fixes a room
      // that has moved on, and nothing about the network will.
      registerDeadline = setTimeout(() => hangUp('room-not-found'), REGISTER_DEADLINE_MS);
      connection.send(pack({ t: 'register-player', id: clientId, roomId }));
    };

    connection.onmessage = (message) => {
      const data = unpack<ForwardedMessage>(message.data);
      if (data.t === 'forward') {
        this.onUpdate(data.payload);
      } else if (data.t === 'connected') {
        clearTimeout(registerDeadline);
        onConnect();
      } else if (data.t === 'room-back') {
        this.onRoomBack?.();
      }
    };

    connection.onclose = (event) => reportClosed(readCloseReason(event), event);
    // An error is always followed by a close, which is what reports - this is only for the log.
    connection.onerror = (event) => onError('error', event);
  }

  public sendEvent(event: NetworkMessages) {
    if (this.connection?.readyState !== WebSocket.OPEN) {
      return;
    }
    this.connection.send(pack({ t: 'forward', recipients: [this.roomId], payload: event }));
  }

  // readyState >=2 means that the connection is closing or closed
  public isConnected = () => (this.connection?.readyState ?? Infinity) < 2;

  /**
   * Deliberately done with this socket - leaving the room, or replacing it with another. Silent:
   * nobody is waiting to hear that a socket they themselves threw away has closed, and reporting it
   * would drive a reconnect for a room the phone has already left.
   */
  public close = () => {
    this.disownCurrent?.();
    this.disownCurrent = null;
    this.hangUpOnCurrent = null;

    const connection = this.connection;
    this.connection = null;
    if (!connection) return;

    connection.onopen = null;
    connection.onmessage = null;
    connection.onclose = null;
    connection.onerror = null;
    connection.close();
  };

  /** The link is dead even if the socket has not admitted it. Closes and reports, so the phone reconnects. */
  public hangUp = (reason: transportCloseReason) => {
    this.hangUpOnCurrent?.(reason);
  };
}

/**
 * The relay closes with a JSON body (`{"error":"peer-unavailable"}`) for anything it decided, and a
 * plain string (`room-disconnected`) for anything that happened to the room. Both are worth keeping
 * - they are how the phone tells "no such game" from "the game is between sockets".
 */
const readCloseReason = (event: CloseEvent): transportCloseReason => {
  if (!event.reason) return 'unknown';

  try {
    return JSON.parse(event.reason)?.error ?? event.reason;
  } catch {
    return event.reason;
  }
};
